import React from "react";
import { Box, Grid, Typography, Button } from "@mui/material";
import { useNavigate } from "react-router-dom";

// ── Animated dashboard-style icon ────────────────────────────────────────────
function DashboardShieldIcon() {
  return (
    <Box sx={{ position: "relative", width: 320, height: 320 }}>
      <svg
        width="320"
        height="320"
        viewBox="0 0 320 320"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <defs>
          <style>{`
            @keyframes spin-slow {
              from { transform: rotate(0deg); }
              to { transform: rotate(360deg); }
            }
            @keyframes spin-reverse {
              from { transform: rotate(0deg); }
              to { transform: rotate(-360deg); }
            }
            @keyframes pulse-ring {
              0%, 100% { opacity: 0.15; }
              50% { opacity: 0.35; }
            }
            @keyframes dash-draw {
              0% { stroke-dashoffset: 280; }
              100% { stroke-dashoffset: 0; }
            }
            @keyframes check-draw {
              0% { stroke-dashoffset: 60; opacity: 0; }
              40% { opacity: 1; }
              100% { stroke-dashoffset: 0; opacity: 1; }
            }
            @keyframes fade-in {
              from { opacity: 0; transform: scale(0.9); }
              to { opacity: 1; transform: scale(1); }
            }
            .ring-outer {
              transform-origin: 160px 160px;
              animation: spin-slow 18s linear infinite;
            }
            .ring-inner {
              transform-origin: 160px 160px;
              animation: spin-reverse 12s linear infinite;
            }
            .pulse-ring {
              animation: pulse-ring 3s ease-in-out infinite;
            }
            .check-path {
              stroke-dasharray: 60;
              animation: check-draw 1.2s cubic-bezier(0.4,0,0.2,1) 0.4s both;
            }
            .main-circle {
              stroke-dasharray: 280;
              animation: dash-draw 1.4s cubic-bezier(0.4,0,0.2,1) both;
            }
            .icon-group {
              animation: fade-in 0.6s ease both;
            }
          `}</style>
        </defs>

        {/* Outermost faint pulse ring */}
        <circle
          cx="160"
          cy="160"
          r="148"
          stroke="rgba(18,178,241,0.12)"
          strokeWidth="1"
          className="pulse-ring"
        />

        {/* Outer dashed orbit ring */}
        <g className="ring-outer">
          <circle
            cx="160"
            cy="160"
            r="134"
            stroke="rgba(18,178,241,0.2)"
            strokeWidth="1.5"
            strokeDasharray="6 10"
            strokeLinecap="round"
          />
          {/* Orbit dot */}
          <circle cx="160" cy="26" r="4" fill="#12b2f1" opacity="0.7" />
        </g>

        {/* Inner dashed orbit ring */}
        <g className="ring-inner">
          <circle
            cx="160"
            cy="160"
            r="110"
            stroke="rgba(18,178,241,0.15)"
            strokeWidth="1"
            strokeDasharray="4 14"
            strokeLinecap="round"
          />
          {/* Orbit dot */}
          <circle cx="160" cy="50" r="3" fill="#12b2f1" opacity="0.5" />
        </g>

        {/* Main filled circle bg */}
        <circle cx="160" cy="160" r="88" fill="#0d2035" />

        {/* Main animated circle stroke */}
        <circle
          cx="160"
          cy="160"
          r="88"
          stroke="#12b2f1"
          strokeWidth="2.5"
          strokeLinecap="round"
          className="main-circle"
          fill="none"
        />

        {/* Status tick marks around the circle (like a dashboard gauge) */}
        {[0, 60, 120, 180, 240, 300].map((angle, i) => {
          const rad = ((angle - 90) * Math.PI) / 180;
          const r1 = 96,
            r2 = 103;
          const x1 = 160 + r1 * Math.cos(rad);
          const y1 = 160 + r1 * Math.sin(rad);
          const x2 = 160 + r2 * Math.cos(rad);
          const y2 = 160 + r2 * Math.sin(rad);
          return (
            <line
              key={i}
              x1={x1}
              y1={y1}
              x2={x2}
              y2={y2}
              stroke="#12b2f1"
              strokeWidth="2"
              strokeLinecap="round"
              opacity="0.6"
            />
          );
        })}

        {/* Icon group: shield + checkmark */}
        <g className="icon-group">
          {/* Shield outline */}
          <path
            d="M160 108 L132 120 L132 152 C132 170 144 184 160 190 C176 184 188 170 188 152 L188 120 Z"
            stroke="white"
            strokeWidth="2.5"
            strokeLinejoin="round"
            fill="none"
            opacity="0.9"
          />
          {/* Checkmark inside shield */}
          <path
            d="M149 154 L157 162 L172 144"
            stroke="#12b2f1"
            strokeWidth="3"
            strokeLinecap="round"
            strokeLinejoin="round"
            fill="none"
            className="check-path"
          />
        </g>

        {/* Small status dots at cardinal points */}
        <circle cx="160" cy="72" r="3.5" fill="#12b2f1" opacity="0.9" />
        <circle cx="248" cy="160" r="3.5" fill="#12b2f1" opacity="0.5" />
        <circle cx="160" cy="248" r="3.5" fill="#12b2f1" opacity="0.3" />
        <circle cx="72" cy="160" r="3.5" fill="#12b2f1" opacity="0.5" />
      </svg>
    </Box>
  );
}

// ── Why component ─────────────────────────────────────────────────────────────
const Why = () => {
  const navigate = useNavigate();

  const handleNavigate = (e) => {
    e.currentTarget.blur();
    navigate("/pricing");
  };

  return (
    <Box sx={{ width: "100%", backgroundColor: "#131a34", overflow: "hidden" }}>
      <Box
        component="section"
        sx={{
          py: { xs: 6, sm: 8, md: 10 },
          pb: { xs: 14, sm: 16 },
          position: "relative",
          maxWidth: 1200,
          width: "100%",
          mx: "auto",
          px: { xs: 2, sm: 3, md: 4 },
        }}
      >
        <Grid container spacing={{ xs: 4, md: 6 }} alignItems="center">
          {/* LEFT CONTENT */}
          <Grid size={{ xs: 12, md: 6 }}>
            <Box>
              <Typography
                sx={{
                  color: "#F59E0B",
                  fontWeight: 800,
                  letterSpacing: "0.08em",
                  textTransform: "uppercase",
                  fontSize: "0.85rem",
                  mb: 1.5,
                }}
              >
                Online Privacy Protection
              </Typography>

              <Typography
                sx={{
                  fontWeight: 800,
                  lineHeight: 1.2,
                  letterSpacing: "-0.015em",
                  fontSize: { xs: "2.8rem", sm: "2.8rem", md: "3.3rem" },
                  color: "white",
                }}
              >
                Your{" "}
                <Box
                  component="span"
                  sx={{ color: "#12b2f1", fontWeight: 700 }}
                >
                  Personal Data
                </Box>{" "}
                is exposed on the web
              </Typography>

              <Typography
                sx={{
                  mt: 2,
                  color: "rgba(255,255,255,0.78)",
                  maxWidth: 720,
                  fontSize: { xs: "0.95rem", sm: "1rem", md: "1.05rem" },
                  lineHeight: 1.6,
                  fontWeight: 400,
                }}
              >
                We build tools that put you back in control by making data
                removal and tracking simple, visible, and reliable.
              </Typography>

              {/* Desktop Button */}
              <Button
                variant="contained"
                size="large"
                onClick={handleNavigate}
                sx={{
                  mt: 4,
                  backgroundColor: "#F59E0B",
                  color: "#0b1220",
                  fontWeight: 900,
                  textTransform: "none",
                  borderRadius: 1,
                  px: 4,
                  "&:hover": { backgroundColor: "#D97706" },
                  "&:focus": { backgroundColor: "#F59E0B" },
                  "&:focus-visible": { backgroundColor: "#F59E0B" },
                  display: { xs: "none", lg: "inline-flex" },
                }}
              >
                Sign Up
              </Button>
            </Box>
          </Grid>

          {/* RIGHT VISUAL */}
          <Grid size={{ xs: 12, md: 6 }}>
            <Box
              sx={{
                width: "100%",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                py: { xs: 6, sm: 8 },
              }}
            >
              <DashboardShieldIcon />
            </Box>
          </Grid>
        </Grid>

        {/* Mobile CTA */}
        <Box
          sx={{
            display: { xs: "flex", lg: "none" },
            justifyContent: "center",
            mt: 4,
          }}
        >
          {/* Mobile Button */}
          <Button
            fullWidth
            onClick={handleNavigate}
            sx={{
              backgroundColor: "#F59E0B",
              color: "#0b1220",
              fontWeight: 900,
              borderRadius: 1.5,
              maxWidth: 420,
              textTransform: "none",
              px: 4,
              py: 1.5,
              "&:hover": { backgroundColor: "#D97706" },
              "&:focus": { backgroundColor: "#F59E0B" },
              "&:focus-visible": { backgroundColor: "#F59E0B" },
            }}
          >
            Sign Up
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default Why;
